import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AspectPicker } from "@/components/create/AspectPicker";
import { QualityPicker } from "@/components/create/QualityPicker";
import { SourceImages } from "@/components/create/SourceImages";
import { api } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { useGenerations } from "@/lib/studio";
import { cn } from "@/lib/utils";

const COPY = {
  create: { placeholder: "Describe the image you want — subject, setting, light, style…", action: "Create" },
  edit: { placeholder: "Describe the change — e.g. “make it a snowy evening” or “put the product on a marble table”", action: "Apply edit" },
};

function RecentPicker({ sources, onPick, max }) {
  const items = (useGenerations().data?.items || []).filter((g) => g.kind === "image" && g.status === "complete" && !sources.some((s) => s.id === g.id)).slice(0, 8);
  if (!items.length || sources.length >= max) return null;
  return (
    <div className="mt-3" data-testid="recent-picker">
      <p className="mb-1.5 text-[11px] text-zinc-500">Or use one of your creations</p>
      <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
        {items.map((g) => (
          <button key={g.id} type="button" onClick={() => onPick({ id: g.id, preview_url: g.thumb_url })} aria-label="Use this creation" data-testid={`recent-pick-${g.id}`}
            className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-[linear-gradient(110deg,#121216_30%,#1e1e24_50%,#121216_70%)] bg-[length:200%_100%] opacity-80 transition-[opacity,transform] animate-shimmer hover:scale-105 hover:opacity-100">
            <img src={g.thumb_url} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function ImageForm({ mode, offer, sources: outerSources, setSources: outerSetSources, initial = {}, onCreated, compact = false }) {
  const qc = useQueryClient();
  const edit = mode === "edit";
  const [ownSources, setOwnSources] = useState([]);
  const sources = outerSources ?? ownSources;
  const setSources = outerSetSources ?? setOwnSources;
  const [prompt, setPrompt] = useState(initial.prompt || "");
  const [quality, setQuality] = useState(initial.quality || "budget");
  const [aspect, setAspect] = useState(initial.aspect || (edit ? "auto" : "1:1"));
  const price = offer.tools[mode]?.[quality];
  const create = useMutation({
    mutationFn: () => api.post("/media/generations", {
      kind: "image", operation: mode, quality, prompt: prompt.trim(), aspect_ratio: aspect, image_ids: edit ? sources.map((s) => s.id) : [],
    }),
    onSuccess: (row) => {
      setPrompt("");
      qc.setQueryData(["media-generations"], (d) => ({ ...d, items: [{ ...row, url: null, thumb_url: null, credits_charged: 0 }, ...(d?.items || [])] }));
      ["media-generations", "media-config", "me"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(edit ? "Editing your image…" : "Creating your image…", { duration: 3000 });
      onCreated?.(row);
    },
    onError: (e) => toast.error(e.message),
  });
  const ready = price?.available && prompt.trim() && (!edit || sources.length > 0) && !create.isPending;
  return (
    <div className={cn("space-y-5", !compact && "rounded-[22px] border border-white/[0.08] bg-ink-850 p-4 sm:p-5")} data-testid={`image-form-${mode}`}>
      {edit && (
        <div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500">{compact ? "Your image" : "Images to edit"} · up to {offer.max_sources}</p>
          <SourceImages sources={sources} onChange={setSources} max={offer.max_sources} />
          <RecentPicker sources={sources} max={offer.max_sources} onPick={(s) => setSources((list) => [...list, s].slice(0, offer.max_sources))} />
        </div>
      )}
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={2000} rows={compact ? 4 : 3} data-testid="create-prompt-input"
        placeholder={COPY[mode].placeholder} aria-label="Prompt"
        className="w-full resize-none rounded-xl border border-white/10 bg-ink-900 p-3 text-sm leading-relaxed text-zinc-100 placeholder:text-zinc-600 transition-colors focus:border-white/25 focus:outline-none" />
      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500">Quality</p>
        <QualityPicker value={quality} onChange={setQuality} prices={offer.tools[mode]} />
      </div>
      <div className={cn("flex flex-col gap-3", !compact && "sm:flex-row sm:items-center")}>
        <AspectPicker options={edit ? offer.edit_aspect_ratios : offer.aspect_ratios} value={aspect} onChange={setAspect} />
        <button type="button" disabled={!ready} onClick={() => create.mutate()} data-testid="create-submit-button"
          className={cn("flex h-11 items-center justify-center gap-2 rounded-full bg-white px-6 text-sm font-semibold text-ink-950 transition-[opacity,transform] hover:opacity-90 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-zinc-500", !compact && "sm:ml-auto")}>
          {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {COPY[mode].action}{price?.available ? ` · ${formatNumber(price.credits)} credits` : ""}
        </button>
      </div>
      {edit && sources.length === 0 && <p className="text-xs text-zinc-600" data-testid="edit-needs-image">Add the image you want to change.</p>}
    </div>
  );
}
